import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { V4FormRow, V4Group, V4Pill, V4Section } from '../ux-v4.js';
import {
  createTerritory,
  deleteTerritory,
  getTerritoryStats,
  listTerritories,
  previewTerritory,
  updateTerritory,
  type TerritoryDto,
  type TerritoryInput,
  type TerritoryPreviewDto,
  type TerritoryStatsDto,
} from '../../lib/acquisition-radar-api.js';
import { CATEGORY_KEYS, categoryLabel, DEAL_TYPE_KEYS, dealTypeLabel, formatCzk, kindLabel, statusLabel } from './labels.js';
import { TerritoryMap, type SjtskPair } from './TerritoryMap.js';
import { canonicalCzechNumber } from '../../lib/czech-number.js';

/**
 * Krok AKV1 — Uzemi: seznam + pridani (obec / cast obce / ORP / okres podle
 * nazvu, okruh kolem bodu, vlastni polygon nakresleny na mape), filtry
 * (kategorie, typ, cena), nahled „kolik inzeratu by uzemi melo" pred
 * ulozenim a statistiky uzemi. Radar chce WGS84 — mapa posila S-JTSK
 * a prevod dela API REOS.
 */

const KINDS: readonly TerritoryDto['kind'][] = ['municipality', 'municipality_part', 'orp', 'district', 'radius', 'polygon'];
const NAMED = new Set<TerritoryDto['kind']>(['municipality', 'municipality_part', 'orp', 'district']);
const PREVIEW_DAYS = 30;

const tr = (key: MessageKey, values?: Record<string, string | number>): string =>
  t(key, { locale: 'cs', ...(values !== undefined ? { values } : {}) });

function errorText(error: unknown): string {
  return error instanceof Error && error.message !== '' ? error.message : tr('webAdmin.acquisitionRadar.saveError');
}

interface Draft {
  name: string;
  kind: TerritoryDto['kind'];
  adminName: string;
  radiusKm: string;
  center: SjtskPair | null;
  polygon: SjtskPair[] | null;
  category: string;
  dealType: string;
  priceMin: string;
  priceMax: string;
}

const EMPTY_DRAFT: Draft = {
  name: '',
  kind: 'municipality',
  adminName: '',
  radiusKm: '5',
  center: null,
  polygon: null,
  category: '',
  dealType: '',
  priceMin: '',
  priceMax: '',
};

function czk(value: string): { amount: string; currency: string } | undefined {
  const digits = value.replace(/\s/g, '');
  return /^\d+$/.test(digits) ? { amount: digits, currency: 'CZK' } : undefined;
}

/** Rozpracovany formular -> vstup API; `null` = jeste chybi povinna cast. */
export function draftToInput(draft: Draft): TerritoryInput | null {
  const name = draft.name.trim() || draft.adminName.trim();
  if (name === '') return null;
  const priceMin = czk(draft.priceMin);
  const priceMax = czk(draft.priceMax);
  const filters = {
    ...(draft.category !== '' ? { category: draft.category } : {}),
    ...(draft.dealType !== '' ? { dealType: draft.dealType } : {}),
    ...(priceMin !== undefined ? { priceMin } : {}),
    ...(priceMax !== undefined ? { priceMax } : {}),
  };
  const base = { name, kind: draft.kind, ...(Object.keys(filters).length > 0 ? { filters } : {}) };
  if (NAMED.has(draft.kind)) {
    if (draft.adminName.trim() === '') return null;
    return { ...base, adminName: draft.adminName.trim() };
  }
  if (draft.kind === 'radius') {
    // Krok UXD2: cteni ceskeho zapisu je SSOT v `lib/czech-number.ts` —
    // misto `replace(',', '.')` tu projde i „2 500\u00a0km" s mezerou v tisicich.
    const radiusKm = Number(canonicalCzechNumber(draft.radiusKm));
    if (draft.center === null || !Number.isFinite(radiusKm) || radiusKm <= 0) return null;
    return { ...base, radiusKm, center: { crs: 'EPSG:5514', coordinates: [draft.center[0], draft.center[1]] } };
  }
  if (draft.polygon === null || draft.polygon.length < 3) return null;
  return { ...base, polygon: { crs: 'EPSG:5514', coordinates: draft.polygon.map((p) => [p[0], p[1]] as [number, number]) } };
}

function filtersSummary(territory: TerritoryDto): string {
  const f = territory.filters;
  const parts = [
    f.category !== null ? categoryLabel(f.category) : null,
    f.dealType !== null ? dealTypeLabel(f.dealType) : null,
    f.priceMin !== null ? tr('webAdmin.acquisitionRadar.territories.priceFrom', { price: formatCzk(f.priceMin.amount) }) : null,
    f.priceMax !== null ? tr('webAdmin.acquisitionRadar.territories.priceTo', { price: formatCzk(f.priceMax.amount) }) : null,
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? tr('webAdmin.acquisitionRadar.territories.noFilters') : parts.join(' · ');
}

export function TerritoriesTab(): React.JSX.Element {
  const [territories, setTerritories] = React.useState<readonly TerritoryDto[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);

  const reload = React.useCallback(() => {
    listTerritories()
      .then(setTerritories)
      .catch((e: unknown) => setError(errorText(e)));
  }, []);

  React.useEffect(reload, [reload]);

  const toggleActive = (territory: TerritoryDto): void => {
    updateTerritory(territory.id, { active: !territory.active })
      .then((updated) => setTerritories((prev) => prev?.map((x) => (x.id === updated.id ? updated : x)) ?? null))
      .catch((e: unknown) => setError(errorText(e)));
  };

  const remove = (territory: TerritoryDto): void => {
    if (!window.confirm(tr('webAdmin.acquisitionRadar.territories.confirmDelete', { name: territory.name }))) return;
    deleteTerritory(territory.id)
      .then(() => setTerritories((prev) => prev?.filter((x) => x.id !== territory.id) ?? null))
      .catch((e: unknown) => setError(errorText(e)));
  };

  return (
    <div className="rh-v4-groups" data-testid="akv-territories">
      {error !== null ? (
        <p className="rh-akv-note rh-akv-note--warn" role="alert" data-testid="akv-territories-error">
          {error}
        </p>
      ) : null}
      {territories === null && error === null ? <p className="rh-akv-hint">{tr('webAdmin.acquisitionRadar.loading')}</p> : null}
      {territories !== null && territories.length === 0 ? (
        <p className="rh-akv-hint" data-testid="akv-territories-empty">
          {tr('webAdmin.acquisitionRadar.territories.empty')}
        </p>
      ) : null}
      {territories?.map((territory) => (
        <TerritoryCard key={territory.id} territory={territory} onToggle={toggleActive} onDelete={remove} />
      ))}

      {adding ? (
        <TerritoryForm
          territories={territories ?? []}
          onCancel={() => setAdding(false)}
          onSaved={(created) => {
            setTerritories((prev) => [...(prev ?? []), created]);
            setAdding(false);
          }}
        />
      ) : (
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--filled rh-v4-btn--block"
          onClick={() => setAdding(true)}
          data-testid="akv-territory-add"
        >
          {tr('webAdmin.acquisitionRadar.territories.add')}
        </button>
      )}
    </div>
  );
}

function TerritoryCard({
  territory,
  onToggle,
  onDelete,
}: {
  readonly territory: TerritoryDto;
  readonly onToggle: (t: TerritoryDto) => void;
  readonly onDelete: (t: TerritoryDto) => void;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [stats, setStats] = React.useState<TerritoryStatsDto | null>(null);
  const [statsError, setStatsError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || stats !== null) return;
    getTerritoryStats(territory.id)
      .then(setStats)
      .catch((e: unknown) => setStatsError(errorText(e)));
  }, [open, stats, territory.id]);

  const where =
    territory.adminName ??
    (territory.radiusKm !== null ? tr('webAdmin.acquisitionRadar.territories.radiusKm', { km: territory.radiusKm }) : kindLabel(territory.kind));

  return (
    <V4Section
      testId={`akv-territory-${String(territory.id)}`}
      label={territory.name}
      sub={`${kindLabel(territory.kind)} · ${where} · ${filtersSummary(territory)}`}
      open={open}
      onToggle={() => setOpen(!open)}
    >
      <div className="rh-akv-detail">
        {statsError !== null ? <p className="rh-akv-note rh-akv-note--warn">{statsError}</p> : null}
        {stats === null && statsError === null ? <p className="rh-akv-hint">{tr('webAdmin.acquisitionRadar.loading')}</p> : null}
        {stats !== null ? (
          <dl className="rh-akv-dl" data-testid={`akv-territory-stats-${String(territory.id)}`}>
            <dt>{tr('webAdmin.acquisitionRadar.territories.statsListings')}</dt>
            <dd>{stats.listingCount}</dd>
            {Object.entries(stats.byCategory).map(([code, count]) => (
              <React.Fragment key={`c-${code}`}>
                <dt>{categoryLabel(code)}</dt>
                <dd>{count}</dd>
              </React.Fragment>
            ))}
            {Object.entries(stats.leadsByStatus).map(([status, count]) => (
              <React.Fragment key={`s-${status}`}>
                <dt>{tr('webAdmin.acquisitionRadar.territories.statsLeads', { status: statusLabel(status as Parameters<typeof statusLabel>[0]) })}</dt>
                <dd>{count}</dd>
              </React.Fragment>
            ))}
          </dl>
        ) : null}
        <p className="rh-akv-actions">
          <button type="button" className="rh-v4-btn rh-v4-btn--tinted" onClick={() => onToggle(territory)}>
            {territory.active
              ? tr('webAdmin.acquisitionRadar.territories.pause')
              : tr('webAdmin.acquisitionRadar.territories.resume')}
          </button>
          <button type="button" className="rh-v4-btn rh-v4-btn--plain" onClick={() => onDelete(territory)}>
            {tr('webAdmin.acquisitionRadar.territories.delete')}
          </button>
          {!territory.active ? <V4Pill tone="warn">{tr('webAdmin.acquisitionRadar.territories.paused')}</V4Pill> : null}
        </p>
      </div>
    </V4Section>
  );
}

function TerritoryForm({
  territories,
  onCancel,
  onSaved,
}: {
  readonly territories: readonly TerritoryDto[];
  readonly onCancel: () => void;
  readonly onSaved: (created: TerritoryDto) => void;
}): React.JSX.Element {
  const [draft, setDraft] = React.useState<Draft>(EMPTY_DRAFT);
  const [preview, setPreview] = React.useState<TerritoryPreviewDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const input = draftToInput(draft);

  const patch = (change: Partial<Draft>): void => {
    setDraft((prev) => ({ ...prev, ...change }));
    setPreview(null);
  };

  const runPreview = (): void => {
    if (input === null) return;
    setError(null);
    previewTerritory(input, PREVIEW_DAYS)
      .then(setPreview)
      .catch((e: unknown) => setError(errorText(e)));
  };

  const save = (): void => {
    if (input === null) return;
    setBusy(true);
    setError(null);
    createTerritory(input)
      .then(onSaved)
      .catch((e: unknown) => setError(errorText(e)))
      .finally(() => setBusy(false));
  };

  const mapMode = draft.kind === 'polygon' ? 'polygon' : draft.kind === 'radius' ? 'radius' : null;
  const radiusKm = Number(canonicalCzechNumber(draft.radiusKm));

  return (
    <V4Group label={tr('webAdmin.acquisitionRadar.territories.formTitle')} testId="akv-territory-form">
      <V4FormRow label={tr('webAdmin.acquisitionRadar.territories.kind')}>
        <select
          className="rh-input"
          value={draft.kind}
          onChange={(e) => patch({ kind: e.target.value as TerritoryDto['kind'], center: null, polygon: null })}
          aria-label={tr('webAdmin.acquisitionRadar.territories.kind')}
          data-testid="akv-territory-kind"
        >
          {KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {kindLabel(kind)}
            </option>
          ))}
        </select>
      </V4FormRow>
      {NAMED.has(draft.kind) ? (
        <V4FormRow label={tr('webAdmin.acquisitionRadar.territories.adminName')} hint={tr('webAdmin.acquisitionRadar.territories.adminNameHint')}>
          <input
            className="rh-input"
            value={draft.adminName}
            onChange={(e) => patch({ adminName: e.target.value })}
            aria-label={tr('webAdmin.acquisitionRadar.territories.adminName')}
            data-testid="akv-territory-admin-name"
          />
        </V4FormRow>
      ) : null}
      {draft.kind === 'radius' ? (
        <V4FormRow label={tr('webAdmin.acquisitionRadar.territories.radius')}>
          <input
            className="rh-input rh-akv-input--short"
            inputMode="decimal"
            value={draft.radiusKm}
            onChange={(e) => patch({ radiusKm: e.target.value })}
            aria-label={tr('webAdmin.acquisitionRadar.territories.radius')}
            data-testid="akv-territory-radius"
          />
        </V4FormRow>
      ) : null}
      {mapMode !== null ? (
        <div className="rh-akv-detail">
          <TerritoryMap
            mode={mapMode}
            radiusKm={Number.isFinite(radiusKm) ? radiusKm : undefined}
            center={draft.center}
            polygon={draft.polygon}
            territories={territories}
            onPolygon={(ring) => patch({ polygon: ring })}
            onCenter={(point) => patch({ center: point })}
          />
          {mapMode === 'polygon' && draft.polygon !== null ? (
            <p className="rh-akv-hint" data-testid="akv-territory-polygon-ok">
              {tr('webAdmin.acquisitionRadar.territories.polygonReady', { count: Math.max(0, draft.polygon.length - 1) })}
            </p>
          ) : null}
          {mapMode === 'radius' && draft.center !== null ? (
            <p className="rh-akv-hint" data-testid="akv-territory-center-ok">
              {tr('webAdmin.acquisitionRadar.territories.centerReady')}
            </p>
          ) : null}
        </div>
      ) : null}
      <V4FormRow label={tr('webAdmin.acquisitionRadar.territories.name')}>
        <input
          className="rh-input"
          value={draft.name}
          placeholder={draft.adminName}
          onChange={(e) => patch({ name: e.target.value })}
          aria-label={tr('webAdmin.acquisitionRadar.territories.name')}
          data-testid="akv-territory-name"
        />
      </V4FormRow>
      <V4FormRow label={tr('webAdmin.acquisitionRadar.territories.category')}>
        <select
          className="rh-input"
          value={draft.category}
          onChange={(e) => patch({ category: e.target.value })}
          aria-label={tr('webAdmin.acquisitionRadar.territories.category')}
          data-testid="akv-territory-category"
        >
          <option value="">{tr('webAdmin.acquisitionRadar.any')}</option>
          {Object.keys(CATEGORY_KEYS).map((code) => (
            <option key={code} value={code}>
              {categoryLabel(code)}
            </option>
          ))}
        </select>
      </V4FormRow>
      <V4FormRow label={tr('webAdmin.acquisitionRadar.territories.dealType')}>
        <select
          className="rh-input"
          value={draft.dealType}
          onChange={(e) => patch({ dealType: e.target.value })}
          aria-label={tr('webAdmin.acquisitionRadar.territories.dealType')}
          data-testid="akv-territory-deal-type"
        >
          <option value="">{tr('webAdmin.acquisitionRadar.any')}</option>
          {Object.keys(DEAL_TYPE_KEYS).map((code) => (
            <option key={code} value={code}>
              {dealTypeLabel(code)}
            </option>
          ))}
        </select>
      </V4FormRow>
      <V4FormRow label={tr('webAdmin.acquisitionRadar.territories.priceMin')}>
        <input
          className="rh-input rh-akv-input--short"
          inputMode="numeric"
          value={draft.priceMin}
          onChange={(e) => patch({ priceMin: e.target.value })}
          aria-label={tr('webAdmin.acquisitionRadar.territories.priceMin')}
          data-testid="akv-territory-price-min"
        />
      </V4FormRow>
      <V4FormRow label={tr('webAdmin.acquisitionRadar.territories.priceMax')}>
        <input
          className="rh-input rh-akv-input--short"
          inputMode="numeric"
          value={draft.priceMax}
          onChange={(e) => patch({ priceMax: e.target.value })}
          aria-label={tr('webAdmin.acquisitionRadar.territories.priceMax')}
          data-testid="akv-territory-price-max"
        />
      </V4FormRow>
      <div className="rh-akv-detail">
        {error !== null ? (
          <p className="rh-akv-note rh-akv-note--warn" role="alert" data-testid="akv-territory-form-error">
            {error}
          </p>
        ) : null}
        {preview !== null ? (
          <p className="rh-akv-note" data-testid="akv-territory-preview">
            {tr('webAdmin.acquisitionRadar.territories.previewResult', { count: preview.listingCount, days: preview.days })}
          </p>
        ) : null}
        <p className="rh-akv-actions">
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--tinted"
            disabled={input === null}
            onClick={runPreview}
            data-testid="akv-territory-preview-btn"
          >
            {tr('webAdmin.acquisitionRadar.territories.preview')}
          </button>
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--filled"
            disabled={input === null || busy}
            onClick={save}
            data-testid="akv-territory-save"
          >
            {tr('webAdmin.acquisitionRadar.territories.save')}
          </button>
          <button type="button" className="rh-v4-btn rh-v4-btn--plain" onClick={onCancel}>
            {tr('webAdmin.acquisitionRadar.cancel')}
          </button>
        </p>
      </div>
    </V4Group>
  );
}

